import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { nativeSportsbookApi } from '@/services/api'
import { DEFAULT_MANIFEST, THEMES, contrastRatio, defaultThemeDocument, themeWithAccent, themeWithHighlight } from './nativeSportsbookManifest'

function stripLabel(theme) {
  if (!theme) return theme
  const rest = { ...theme }
  delete rest.label
  return rest
}
function normalizeThemeDraft(document) {
  if (!document) return document
  const draft = stripLabel(structuredClone(document))
  if (draft.colors && !draft.colors.highlight) {
    draft.colors.highlight = '#FFD166'
  }
  if (draft.appearance?.schemes?.light && !draft.appearance.schemes.light.highlight) {
    draft.appearance.schemes.light = {
      ...draft.appearance.schemes.light,
      highlight: '#8A6100',
    }
  }
  return draft
}

// A theme document only carries the appearance/scheme shape this page edits
// once it has been saved with the new theme feature; earlier saves predate it.
function seedFromCurrent(current) {
  if (current?.document?.appearance?.schemes) {
    return {
      draft: normalizeThemeDraft(current.document),
      dirty: false,
      notice: '',
      noticeTone: 'neutral',
    }
  }
  if (current?.document) {
    return {
      draft: defaultThemeDocument('betparxLight'),
      dirty: true,
      notice: 'The saved theme predates appearance schemes. Review it and save to upgrade.',
      noticeTone: 'warning',
    }
  }
  return {
    draft: defaultThemeDocument('betparxLight'),
    dirty: false,
    notice: 'No brand theme saved yet. Save to create theme r1.',
    noticeTone: 'neutral',
  }
}

function publishableTheme(draft) {
  return normalizeThemeDraft(draft)
}

export function useNativeSportsbookTheme(slug) {
  const queryClient = useQueryClient()
  // The Home page shares this query key, so arriving from it renders with the
  // theme already cached; seed synchronously so the first frame is correct.
  const [seed] = useState(() => {
    const cached = queryClient.getQueryData(['native-sportsbook', slug, 'theme'])
    return cached ? { current: cached.current ?? null, ...seedFromCurrent(cached.current ?? null) } : null
  })
  const [draft, setDraft] = useState(() => seed?.draft ?? defaultThemeDocument('betparxLight'))
  const [selectedRevision, setSelectedRevision] = useState(() => seed?.current ?? null)
  const [dirty, setDirty] = useState(() => seed?.dirty ?? false)
  const [notice, setNotice] = useState(() => seed?.notice ?? '')
  const [noticeTone, setNoticeTone] = useState(() => seed?.noticeTone ?? 'neutral')
  const initializedRef = useRef(Boolean(seed))

  const themeQuery = useQuery({ queryKey: ['native-sportsbook', slug, 'theme'], queryFn: () => nativeSportsbookApi.getTheme(slug), enabled: Boolean(slug) })
  const manifestsQuery = useQuery({ queryKey: ['native-sportsbook', slug, 'manifests'], queryFn: () => nativeSportsbookApi.listManifests(slug), enabled: Boolean(slug) })

  const current = themeQuery.data?.current ?? null
  const revisions = useMemo(() => themeQuery.data?.revisions || [], [themeQuery.data?.revisions])
  const manifests = useMemo(() => manifestsQuery.data?.manifests || [], [manifestsQuery.data?.manifests])

  useEffect(() => {
    if (initializedRef.current || !themeQuery.isSuccess) return
    initializedRef.current = true
    const seed = seedFromCurrent(current)
    setDraft(seed.draft)
    setSelectedRevision(current)
    setDirty(seed.dirty)
    setNotice(seed.notice)
    setNoticeTone(seed.noticeTone)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeQuery.isSuccess])

  const touch = () => setDirty(true)

  const selectPreset = (key) => {
    const preset = THEMES[key]
    if (!preset) return
    setDraft((prior) => ({
      ...prior,
      name: preset.name,
      colors: structuredClone(preset.colors),
      appearance: structuredClone(preset.appearance),
      typography: structuredClone(preset.typography),
    }))
    touch()
  }

  const setDefaultAppearance = (value) => {
    setDraft((prior) => ({ ...prior, appearance: { ...prior.appearance, default: value } }))
    touch()
  }

  const setAccent = (appearance, accent) => {
    setDraft((prior) => themeWithAccent(prior, appearance, accent))
    touch()
  }

  const setHighlight = (appearance, highlight) => {
    setDraft((prior) => themeWithHighlight(prior, appearance, highlight))
    touch()
  }

  const loadRevision = (revision) => {
    setDraft(normalizeThemeDraft(revision.document))
    setSelectedRevision(revision)
    const isCurrent = current?.revision === revision.revision
    setDirty(!isCurrent)
    setNotice(isCurrent ? `Loaded theme r${revision.revision}.` : `Loaded theme r${revision.revision}. Save to make it current.`)
    setNoticeTone('neutral')
  }

  const saveMutation = useMutation({
    mutationFn: () => nativeSportsbookApi.saveTheme(slug, publishableTheme(draft)),
    onSuccess: (theme) => {
      queryClient.setQueryData(['native-sportsbook', slug, 'theme'], (prior) => ({
        current: theme,
        revisions: [theme, ...(prior?.revisions || []).filter((revision) => revision.revision !== theme.revision)],
      }))
      setSelectedRevision(theme)
      setDraft(normalizeThemeDraft(theme.document))
      setDirty(false)
      setNotice(`Saved theme r${theme.revision}. Home screens pick it up on their next save.`)
      setNoticeTone('success')
      queryClient.invalidateQueries({ queryKey: ['native-sportsbook', slug] })
    },
  })

  const contrast = useMemo(() => ({
    dark: contrastRatio(draft?.colors?.accent, draft?.colors?.background),
    light: contrastRatio(draft?.appearance?.schemes?.light?.accent, draft?.appearance?.schemes?.light?.background),
    highlightDark: contrastRatio(draft?.colors?.highlight || '#FFD166', draft?.colors?.background),
    highlightLight: contrastRatio(draft?.appearance?.schemes?.light?.highlight || '#8A6100', draft?.appearance?.schemes?.light?.background),
  }), [draft])

  const canSave = dirty &&
    contrast.dark >= 4.5 &&
    contrast.light >= 4.5 &&
    contrast.highlightDark >= 4.5 &&
    contrast.highlightLight >= 4.5

  const homeDocument = manifests[0]?.document ?? DEFAULT_MANIFEST
  const usage = useMemo(() => manifests.slice(0, 5).map((manifest) => ({
    revision: manifest.revision,
    theme_revision: manifest.theme_revision ?? null,
    status: manifest.status,
  })), [manifests])
  const homeOutdated = Boolean(current && manifests[0] && (manifests[0].theme_revision ?? 0) < current.revision)

  return {
    draft, current, selectedRevision, revisions, dirty, notice, noticeTone, contrast, canSave,
    themeQuery, manifestsQuery, homeDocument, usage, homeOutdated,
    selectPreset, setDefaultAppearance, setAccent, setHighlight, loadRevision, saveMutation,
  }
}
